import { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Route navigation owns window scrolling. Game state, filters on the same page,
// and sheets never trigger a reset. Back/forward restores the visited entry.
export default function NavigationScroll() {
  const location = useLocation(), navigation = useNavigationType();
  const positions = useRef(new Map()), previous = useRef(null);
  useLayoutEffect(() => {
    const prior = window.history.scrollRestoration;
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const remember = () => { if (previous.current) positions.current.set(previous.current.key, { x: window.scrollX, y: window.scrollY }); };
    window.addEventListener('scroll', remember, { passive: true });
    return () => { window.removeEventListener('scroll', remember); if ('scrollRestoration' in window.history) window.history.scrollRestoration = prior; };
  }, []);
  useLayoutEffect(() => {
    const old = previous.current;
    // A shorter destination can clamp scrollY during DOM replacement. Prefer the
    // last scroll event from the departing entry over that already-clamped value.
    if (old && !positions.current.has(old.key)) positions.current.set(old.key, { x: window.scrollX, y: window.scrollY });
    previous.current = { key: location.key, pathname: location.pathname, hash: location.hash };
    const target = navigation === 'POP' ? positions.current.get(location.key) : null;
    let fragment = '';
    try { fragment = decodeURIComponent(location.hash.slice(1)); } catch { /* Invalid anchors fall back to page top. */ }
    if (!target && !fragment) {
      if (!old || old.pathname !== location.pathname || old.hash) window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      return undefined;
    }
    let observer, resize, timeout, stopped = false;
    const stop = () => { stopped = true; observer?.disconnect(); resize?.disconnect(); clearTimeout(timeout); for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.removeEventListener(event, stop); };
    const restore = () => {
      if (stopped) return;
      if (target) {
        window.scrollTo({ left: target.x, top: target.y, behavior: 'instant' });
        if (Math.abs(window.scrollY - target.y) < 2) stop();
      } else {
        const node = document.getElementById(fragment) || document.getElementsByName(fragment)[0];
        if (node) { node.scrollIntoView({ behavior: 'instant', block: 'start' }); stop(); }
      }
    };
    restore();
    if (!stopped) {
      observer = new MutationObserver(restore); observer.observe(document.body, { childList: true, subtree: true });
      if (typeof ResizeObserver !== 'undefined') { resize = new ResizeObserver(restore); resize.observe(document.documentElement); }
      timeout = setTimeout(stop, 2500);
      for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.addEventListener(event, stop, { once: true, passive: true });
    }
    return stop;
  }, [location.key, location.pathname, location.hash, navigation]);
  return null;
}
